import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight, ShoppingBag, Gift, Star } from "lucide-react";
import { useCart } from "../context/CartContext";
import SeasonalDiscountRow from "../components/SeasonalDiscountRow";
import { useAuth } from "../context/AuthContext";
import ProductImage from "../components/ProductImage";

export default function Cart() {
  const {
    detailed, subtotal, updateQty, removeItem,
    starterKitThreshold, qualifiesStarterKit, remainingForStarterKit,
    volumeDiscountPct, volumeDiscountAmount,
    promoCode, appliedCoupons, removePromoCode, clearPromoCode,
    personalDiscountPct, personalDiscountAmount, personalDiscountLabel,
    totalDiscountAmount, discountedTotal, seasonalPromo, promoCodes,
  } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  const hasUnavailable = detailed.some(
    (d) => !d.variant.inStock || d.variant.stock <= 0 || d.qty > d.variant.stock
  );

  const handleCheckout = () => {
    setError("");
    if (detailed.length === 0) return;
    if (hasUnavailable) {
      setError("One or more items in your cart are unavailable or exceed available stock. Please update your cart.");
      return;
    }
    navigate("/checkout/review");
  };

  if (detailed.length === 0) {
    return (
      <main data-testid="cart-page" className="py-20 md:py-28">
        <div className="container-x text-center max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-7 h-7 text-sage-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-4xl font-bold text-charcoal-600 mb-3">Your cart is empty</h1>
          <p className="text-charcoal-400 mb-8">Browse our research compound catalog to get started.</p>
          <Link to="/products" data-testid="empty-cart-browse-link" className="btn-primary">
            Browse Compounds <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="cart-page" className="py-12 md:py-16">
      <div className="container-x">
        <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 mb-6">Your research cart</h1>

        {user?.is_affiliate && Number(user?.affiliate_personal_discount_pct) > 0 && (
          <div
            data-testid="cart-affiliate-banner"
            className="mb-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 md:p-5 flex items-start gap-3"
          >
            <Star className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex-1 text-sm text-emerald-900">
              <div className="font-semibold mb-0.5">
                Your affiliate discount is applied.
              </div>
              <div className="text-emerald-800/90">
                {user.affiliate_personal_discount_pct}% off automatically applied to every order.
                {user.referral_code && (
                  <> Share code <strong className="font-mono">{user.referral_code}</strong> to earn commission.</>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-4">
            {detailed.map((item) => {
              const lineKey = `${item.slug}-${item.variant.id}`;
              return (
                <div
                  key={lineKey}
                  data-testid={`cart-row-${lineKey}`}
                  className="surface-card p-4 md:p-5 flex gap-4 md:gap-5 items-center"
                >
                  <Link to={`/product/${item.slug}`} className="shrink-0">
                    <ProductImage
                      src={item.image}
                      fallback={item.imageFallback}
                      alt={item.name}
                      className="w-20 h-24 md:w-24 md:h-28 object-cover rounded-2xl"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.slug}`}>
                      <h3 className="font-display font-bold text-charcoal-600 text-lg leading-tight hover:text-sage-600 transition-colors">
                        {item.name}
                      </h3>
                    </Link>
                    <p className="text-xs text-charcoal-400 mt-0.5 font-medium">{item.variant.label}</p>
                    <p className="badge-compliance mt-2">Research Use Only</p>
                    {(!item.variant.inStock || item.variant.stock <= 0) && (
                      <p data-testid={`cart-out-${lineKey}`} className="mt-2 text-xs text-red-600 font-medium">
                        Out of stock. Please remove
                      </p>
                    )}
                    {item.variant.inStock && item.variant.stock > 0 && item.qty > item.variant.stock && (
                      <p data-testid={`cart-overstock-${lineKey}`} className="mt-2 text-xs text-amber-700 font-medium">
                        Only {item.variant.stock} available. Reduce quantity
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <span className="font-display font-bold text-charcoal-600">
                      ${item.lineTotal.toFixed(2)}
                    </span>
                    <div className="inline-flex items-center bg-cream-200 rounded-full p-0.5">
                      <button
                        data-testid={`cart-decrement-${lineKey}`}
                        onClick={() => updateQty(item.slug, item.variant.id, item.qty - 1)}
                        className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center"
                      >
                        <Minus className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                      <span className="w-7 text-center text-sm font-medium">{item.qty}</span>
                      <button
                        data-testid={`cart-increment-${lineKey}`}
                        onClick={() => updateQty(item.slug, item.variant.id, item.qty + 1)}
                        className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                    <button
                      data-testid={`cart-remove-${lineKey}`}
                      onClick={() => removeItem(item.slug, item.variant.id)}
                      className="text-xs text-charcoal-300 hover:text-charcoal-600 inline-flex items-center gap-1"
                    >
                      <X className="w-3 h-3" strokeWidth={2} /> Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-4">
            <div className="surface-card p-6 md:p-7 sticky top-28">
              <h2 className="font-display font-bold text-xl text-charcoal-600 mb-5">Order summary</h2>
              <div className="space-y-3 text-sm text-charcoal-500">
                <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                <SeasonalDiscountRow
                  promo={seasonalPromo}
                  testIdPrefix="cart"
                  codesIgnored={(promoCodes || []).length > 0}
                />

                {volumeDiscountPct > 0 && (
                  <div
                    data-testid="cart-volume-discount-row"
                    className="flex justify-between items-baseline text-sage-700"
                  >
                    <span className="flex items-center gap-1.5">
                      Volume discount
                      <span className="text-[10px] font-medium tracking-wider uppercase bg-sage-100 text-sage-700 px-1.5 py-0.5 rounded-full">
                        {volumeDiscountPct}% off
                      </span>
                    </span>
                    <span data-testid="cart-volume-discount-amount" className="font-medium">
                      −${volumeDiscountAmount.toFixed(2)}
                    </span>
                  </div>
                )}

                {(appliedCoupons || []).map((c) => (
                  <div
                    key={c.code}
                    data-testid={`cart-promo-discount-row-${c.code}`}
                    className="flex justify-between items-baseline text-[#b14a1f]"
                  >
                    <span className="flex items-center gap-1.5">
                      Promo
                      <span className="text-[10px] font-medium tracking-wider uppercase bg-[#fde6ea] text-[#b14a1f] px-1.5 py-0.5 rounded-full">
                        {c.code} · {c.type === "amount"
                          ? `$${Number(c.amount_off).toFixed(2)}`
                          : `${c.pct}%`}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePromoCode(c.code)}
                        data-testid={`cart-remove-promo-${c.code}`}
                        aria-label={`Remove ${c.code}`}
                        className="text-[11px] text-charcoal-400 hover:text-charcoal-600 underline underline-offset-2"
                      >
                        remove
                      </button>
                    </span>
                    <span
                      data-testid={`cart-promo-discount-amount-${c.code}`}
                      className="font-medium"
                    >
                      −${Number(c.discount_amount).toFixed(2)}
                    </span>
                  </div>
                ))}

                {personalDiscountPct > 0 && personalDiscountAmount > 0 && (
                  <div
                    data-testid="cart-personal-discount-row"
                    className="flex justify-between items-baseline text-plum-700"
                    style={{ color: "#8B2252" }}
                  >
                    <span className="flex items-center gap-1.5">
                      {personalDiscountLabel}
                      <span
                        className="text-[10px] font-medium tracking-wider uppercase px-1.5 py-0.5 rounded-full"
                        style={{ background: "rgba(139,34,82,0.10)", color: "#8B2252" }}
                      >
                        {personalDiscountPct}% off
                      </span>
                    </span>
                    <span data-testid="cart-personal-discount-amount" className="font-medium">
                      −${personalDiscountAmount.toFixed(2)}
                    </span>
                  </div>
                )}

                <Row label="Shipping" value="Calculated at checkout" muted />
                <div className="h-px bg-charcoal-100" />
                <div className="flex justify-between items-center pt-1">
                  <span className="font-display font-bold text-charcoal-600">Total</span>
                  <span data-testid="cart-total" className="font-display font-bold text-2xl text-charcoal-600">
                    ${discountedTotal.toFixed(2)}
                  </span>
                </div>
                {totalDiscountAmount > 0 && (
                  <p
                    data-testid="cart-discount-note"
                    className="text-[11px] text-charcoal-400 leading-snug -mt-1"
                  >
                    You&apos;re saving ${totalDiscountAmount.toFixed(2)}. Discount automatically applied at checkout.
                  </p>
                )}
              </div>

              {error && (
                <div data-testid="checkout-error" className="mt-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Starter Kit progress */}
              <div
                data-testid="starter-kit-progress"
                className={`mt-5 rounded-2xl p-4 border ${
                  qualifiesStarterKit
                    ? "bg-sage-100 border-sage-200/60"
                    : "bg-cream-200/70 border-charcoal-100/60"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${
                      qualifiesStarterKit
                        ? "bg-sage-500 text-white"
                        : "bg-white text-sage-600 border border-sage-200"
                    }`}
                  >
                    <Gift className="w-3.5 h-3.5" strokeWidth={2.2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    {qualifiesStarterKit ? (
                      <p data-testid="starter-kit-qualified" className="text-sm text-charcoal-600 font-medium leading-snug">
                        Free Research Starter Kit unlocked. Details included with your order confirmation.
                      </p>
                    ) : (
                      <>
                        <p data-testid="starter-kit-remaining" className="text-sm text-charcoal-600 leading-snug">
                          Add <span className="font-display font-bold">${remainingForStarterKit.toFixed(2)}</span> more to unlock a free Research Starter Kit.
                        </p>
                        <div className="mt-2.5 h-1.5 rounded-full bg-white overflow-hidden">
                          <div
                            className="h-full bg-sage-400 transition-all duration-500"
                            style={{
                              width: `${Math.min(100, (subtotal / starterKitThreshold) * 100).toFixed(1)}%`,
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <button
                data-testid="checkout-button"
                onClick={handleCheckout}
                disabled={hasUnavailable}
                className="btn-primary w-full mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {hasUnavailable ? "Resolve cart issues" : "Proceed to Checkout"}
                {!hasUnavailable && <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />}
              </button>

              <p className="mt-5 text-[11px] text-charcoal-300 leading-relaxed">
                By placing an order, you confirm you are a qualified researcher purchasing for laboratory use only. For Research Use Only. Not for Human Consumption.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

const Row = ({ label, value, muted }) => (
  <div className="flex justify-between">
    <span className={muted ? "text-charcoal-300" : ""}>{label}</span>
    <span className={`font-medium ${muted ? "text-charcoal-300" : "text-charcoal-600"}`}>{value}</span>
  </div>
);
